import type { ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { TextField } from '@garnish/forms';

export const AddressSearchField = (props: AddressSearchFieldProps) => {
  const { value, isRequired, shouldFocusInput, hasResults } = props;
  const { formatMessage } = useIntl();

  // ──── Invalid address notice ───────────────────────────────
  const hasTypedValidAddress = (value ?? '').length > 3;
  const isInvalidAddress = hasTypedValidAddress && !hasResults;
  const invalidNotice = formatMessage(messages.invalidAddressNotice);

  return (
    <TextField
      hideSubmitButton
      autoFocus={shouldFocusInput}
      style={styles.field}
      testID="address-search-autocomplete"
      autoComplete="street-address"
      clearButtonType="text"
      label={formatMessage(messages.addressSearchPlaceholder)}
      clearBtnLabel={formatMessage(messages.clearButtonLabel)}
      clearBtnAccessibilityHint={formatMessage(messages.clearButtonA11yHint)}
      submitBtnAccessibilityLabel={formatMessage(messages.submitButtonLabel)}
      submitAccessibilityHint={formatMessage(messages.submitButtonA11yHint)}
      invalid={isRequired ? isInvalidAddress : undefined}
      notice={isInvalidAddress ? invalidNotice : ''}
      {...props}
    />
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addressSearchPlaceholder: {
    defaultMessage: 'Enter your delivery address',
    description: 'Account | Account Form | Enter your delivery address',
  },
  invalidAddressNotice: {
    defaultMessage: 'Enter a valid address',
    description: 'Account | Address Form | Enter a valid address',
  },
  clearButtonLabel: {
    defaultMessage: 'Clear',
    description: 'Account | Address Form | Clear',
  },
  clearButtonA11yHint: {
    defaultMessage: 'Clears entered text',
    description: 'Account | Address Form | A11y label | Clears entered text',
  },
  submitButtonLabel: {
    defaultMessage: 'Submit',
    description: 'Account | Address Form | Submit',
  },
  submitButtonA11yHint: {
    defaultMessage: 'Submits entered text',
    description: 'Account | Address Form | A11y label | Submits entered text',
  },
});

// ─── Styles ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  field: {
    minHeight: 42,
  },
});

// ──── Types ──────────────────────────────────────────────────

type AddressSearchFieldProps = Readonly<{
  isRequired?: boolean;
  shouldFocusInput?: boolean;
  hasResults?: boolean;
}> &
  ComponentProps<typeof TextField>;
